"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { useAuth } from "@/app/hooks";
import { logger } from "@/app/lib/logger";
import type { ChatMessage } from "@/types";
import { NAVIGATION_DELAY_MS } from "./chatStreaming/constants";
import { useChatSubmission } from "./chatStreaming/useChatSubmission";
import type { HistoryViewGuard, ViewScope } from "./chatStreaming/useHistoryViewGuard";
import { useResponsePersistence } from "./chatStreaming/useResponsePersistence";
import { useStreamReconnect } from "./chatStreaming/useStreamReconnect";

interface UseChatStreamingProps {
   history: ChatMessage[];
   setHistory: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
   ownerShareId: string;
   shareKey?: string;
   studentChatHistoryId?: string;
   activeHistoryId: string;
   handleCreateNewChat: (newHistory: ChatMessage[]) => Promise<void>;
   handleAddToHistory: (
      newHistory: ChatMessage[],
      historyChatId?: string
   ) => Promise<void>;
   messageListRef: React.RefObject<HTMLDivElement | null>;
   messagesEndRef: React.RefObject<HTMLDivElement | null>;
   wrapMessageHeightRef: React.RefObject<HTMLDivElement | null>;
   inputRefFocusAfterFinishChat?: React.RefObject<HTMLTextAreaElement | null>;
   viewGuard: HistoryViewGuard;
   newChatIdAssignedRef: React.MutableRefObject<string | null>;
}

export const useChatStreaming = ({
   history,
   setHistory,
   ownerShareId,
   shareKey,
   studentChatHistoryId,
   activeHistoryId,
   handleCreateNewChat,
   handleAddToHistory,
   inputRefFocusAfterFinishChat,
   viewGuard,
   newChatIdAssignedRef
}: UseChatStreamingProps) => {
   const locale = useLocale();
   const translation = useTranslations("Home");
   const params = useParams();
   const { user, checkAuth } = useAuth();
   const { selectedBaseKnowledge } = useAiSourceSelector();

   const urlChatId =
      typeof params?.id === "string" && params.id !== "temporary" ? params.id : "";

   const [streamingResponse, setStreamingResponse] = useState("");

   const {
      historyItem,
      setHistoryItem,
      selectedModel,
      selectedTemperature,
      selectedStreaming,
      loadingChat: loading,
      setLoadingChat: setLoading,
      triggerHistoryRefetch,
      triggerUsageRefresh,
      isTemporaryChat,
      setCurrentAttachments,
      currentAttachments,
      setErrorChat: setError,
      addActiveGeneration,
      removeActiveGeneration
   } = useStateHistoryChatPage();

   const isSavingRef = useRef(false);
   const currentReader = useRef<ReadableStreamDefaultReader | null>(null);
   const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);
   const streamingHistoryRef = useRef<string | null>(null);
   const isCreatingNewChatRef = useRef(false);
   const isUnmountingRef = useRef(false);
   const isReconnectingRef = useRef(false);
   const abortControllerRef = useRef<AbortController | null>(null);

   const saveResponseToHistory = useResponsePersistence({
      isSavingRef,
      isCreatingNewChatRef,
      currentAttachments,
      setHistory,
      setCurrentAttachments,
      setStreamingResponse,
      isTemporaryChat,
      ownerShareId,
      userId: user?.id,
      historyItemId: historyItem?.id,
      handleAddToHistory,
      handleCreateNewChat
   });

   const clearChatStates = useCallback(() => {
      setStreamingResponse("");
      streamingHistoryRef.current = null;
      setError(null);
      // Loading is managed by chat() and attemptStreamReconnect().
   }, [setStreamingResponse, setError]);

   const markCurrentChatUpdated = useCallback(() => {
      const storeState = useStateHistoryChatPage.getState();
      const chatHistoryId =
         streamingHistoryRef.current || storeState.historyItem?.id || activeHistoryId;
      if (!chatHistoryId) return;

      const updatedAt = new Date().toISOString();
      useStateHistoryChatPage.setState((state) => {
         const nextState: Partial<typeof state> = {};
         const historyItemMatches = state.historyItem?.id === chatHistoryId;

         if (historyItemMatches && state.historyItem) {
            nextState.historyItem = {
               ...state.historyItem,
               updatedAt
            };
         }

         return nextState;
      });
      storeState.queueHistoryPatch({ id: chatHistoryId, lastMessageAt: updatedAt });
   }, [activeHistoryId]);

   const markCurrentChatUpdatedRef = useRef(markCurrentChatUpdated);
   markCurrentChatUpdatedRef.current = markCurrentChatUpdated;

   const handleMarkChatUpdated = useCallback(() => {
      markCurrentChatUpdatedRef.current();
   }, []);

   const stopChatStreaming = useCallback(
      async (partialResponse?: string, scope?: ViewScope) => {
         try {
            if (currentReader.current) {
               await currentReader.current.cancel();
               currentReader.current = null;
            }
            if (timeoutRef.current) {
               clearInterval(timeoutRef.current);
               timeoutRef.current = null;
            }
            streamingHistoryRef.current = null;

            if (partialResponse && !isSavingRef.current) {
               await saveResponseToHistory(partialResponse, { scope });
            }
         } catch (error) {
            logger.error("[ChatStream] Error stopping chat stream:", error);
         }
      },
      []
   );

   const attemptStreamReconnect = useStreamReconnect({
      history,
      currentReader,
      timeoutRef,
      streamingHistoryRef,
      isUnmountingRef,
      isReconnectingRef,
      abortControllerRef,
      setLoading,
      setStreamingResponse,
      setError,
      setCurrentAttachments,
      setHistory,
      selectedBaseKnowledgeId: selectedBaseKnowledge.id,
      triggerUsageRefresh,
      stopChatStreaming,
      removeActiveGeneration,
      addActiveGeneration,
      translation,
      markCurrentChatUpdated: handleMarkChatUpdated,
      viewGuard
   });

   const chat = useChatSubmission({
      history,
      setHistory,
      selectedModel,
      selectedTemperature,
      selectedStreaming,
      selectedBaseKnowledgeId: selectedBaseKnowledge.id,
      isTemporaryChat,
      historyItemId: historyItem?.id,
      shareKey,
      checkAuth,
      translation,
      addActiveGeneration,
      saveResponseToHistory,
      stopChatStreaming,
      markCurrentChatUpdated: handleMarkChatUpdated,
      setLoading,
      setError,
      triggerHistoryRefetch,
      triggerUsageRefresh,
      locale,
      studentChatHistoryId,
      removeActiveGeneration,
      inputRefFocusAfterFinishChat,
      setHistoryItem,
      setCurrentAttachments,
      setStreamingResponse,
      viewGuard,
      newChatIdAssignedRef,
      currentReader,
      timeoutRef,
      streamingHistoryRef,
      isSavingRef,
      isUnmountingRef,
      abortControllerRef
   });

   useEffect(() => {
      isUnmountingRef.current = false;

      const handlePageUnload = () => {
         isUnmountingRef.current = true;
      };
      window.addEventListener("beforeunload", handlePageUnload);
      window.addEventListener("pagehide", handlePageUnload);

      return () => {
         window.removeEventListener("beforeunload", handlePageUnload);
         window.removeEventListener("pagehide", handlePageUnload);
         isUnmountingRef.current = true;
      };
   }, []);

   useEffect(() => {
      const chatId = activeHistoryId || urlChatId;
      if (!chatId || shareKey) return;

      let cancelled = false;

      const checkAndReconnect = () => {
         if (cancelled) return;
         logger.info("[StreamReconnect] Probing server for active generation", {
            chatId,
            source: activeHistoryId ? "activeHistoryId" : "url-fallback"
         });
         attemptStreamReconnect(chatId);
      };

      const persistApi = useStateHistoryChatPage.persist;
      if (persistApi.hasHydrated()) {
         const timer = setTimeout(checkAndReconnect, NAVIGATION_DELAY_MS);
         return () => {
            cancelled = true;
            clearTimeout(timer);
         };
      }

      logger.debug("[StreamReconnect] Waiting for Zustand hydration...");
      const unsubscribe = persistApi.onFinishHydration(() => {
         logger.debug("[StreamReconnect] Zustand hydrated, probing server");
         setTimeout(checkAndReconnect, NAVIGATION_DELAY_MS);
      });

      return () => {
         cancelled = true;
         unsubscribe();
      };
   }, [activeHistoryId, shareKey, attemptStreamReconnect, urlChatId]);

   useEffect(() => {
      const reconnectingChatId = streamingHistoryRef.current;
      const isSameChatTransition =
         reconnectingChatId && activeHistoryId && reconnectingChatId === activeHistoryId;

      if (!isSameChatTransition) {
         setLoading(false);
      }
      streamingHistoryRef.current = activeHistoryId || null;

      return () => {
         const currentUrlId = urlChatId;
         const isReconnectingSameChat =
            isReconnectingRef.current &&
            (streamingHistoryRef.current === activeHistoryId ||
               streamingHistoryRef.current === currentUrlId);

         if (isReconnectingSameChat) {
            logger.debug("[StreamReconnect] Skipping abort — same chat transition", {
               streamingId: streamingHistoryRef.current,
               activeHistoryId,
               currentUrlId
            });
         } else {
            isReconnectingRef.current = false;
            if (abortControllerRef.current) {
               logger.debug("[StreamReconnect] Aborting active fetch on chat switch");
               abortControllerRef.current.abort();
               abortControllerRef.current = null;
            }
            if (currentReader.current) {
               logger.debug("[StreamReconnect] Cancelling active reader on chat switch");
               currentReader.current.cancel().catch(() => {});
               currentReader.current = null;
            }
            if (timeoutRef.current) {
               clearInterval(timeoutRef.current);
               timeoutRef.current = null;
            }
         }
      };
   }, [activeHistoryId, setLoading, urlChatId]);

   useEffect(() => {
      const handleVisibilityChange = () => {
         if (document.visibilityState !== "visible") return;

         const chatId = activeHistoryId || urlChatId;
         if (!chatId || shareKey) return;

         if (currentReader.current || isReconnectingRef.current) return;

         logger.info(
            "[StreamReconnect] Tab became visible, probing server for active stream",
            {
               chatId
            }
         );
         attemptStreamReconnect(chatId);
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () =>
         document.removeEventListener("visibilitychange", handleVisibilityChange);
   }, [activeHistoryId, shareKey, attemptStreamReconnect, urlChatId]);

   return {
      streamingResponse,
      setStreamingResponse,
      loading,
      setError,
      clearChatStates,
      stopChatStreaming,
      attemptStreamReconnect,
      chat,
      saveResponseToHistory,
      isReconnectingRef,
      isCreatingNewChatRef
   };
};
